import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import { TFunction } from 'i18next';
import React from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ProjectUserTagV1Model } from '../../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useProjectSnapshot } from '../../hooks/use-project-snapshot';
import { reset } from '../../utils/styled-reset';
import { Icon } from '../icon/icon';
import { InfoBox } from '../info-box/info-box';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { Stack } from '../stack/stack';
import { Switch } from '../switch/switch';
import { Tooltip } from '../tooltip/tooltip';

enum Status {
  Idle,
  Loading,
  Saving,
  Error,
}

interface User {
  id: string;
  name: string;
}

interface Props {
  user: User;
}

const ProjectUserTags = (props: Props) => {
  const { project } = useProjectSnapshot();
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(Status.Idle);
  const [userTags, setUserTags] = React.useState<ProjectUserTagV1Model[]>([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-end',
    strategy: 'fixed',
  });

  React.useEffect(() => {
    if (showDropdown && refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [showDropdown, refs.reference, refs.floating, update]);

  React.useEffect(() => {
    if (!showDropdown) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        refs.floating.current?.contains(target) === false &&
        (refs.reference.current as HTMLElement | null)?.contains(target) === false
      ) {
        setShowDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown, refs.floating, refs.reference]);

  React.useEffect(() => {
    if (!project) {
      return;
    }
    const load = async () => {
      try {
        setStatus(Status.Loading);
        setUserTags([]);

        const tags = await SkyMapAxiosServiceFactory.instance
          .createProjectServiceV0()
          .getProjectUserTagsV1({
            path: {
              projectId: project.id,
              userId: props.user.id,
            },
          });

        setUserTags(
          tags.data.map((x) => ({
            permission: x.permission,
            tagId: x.tagId,
            projectId: x.projectId,
          })),
        );

        setStatus(Status.Idle);
      } catch (err) {
        setStatus(Status.Error);
      }
    };

    if (props.user) {
      void load();
    }
  }, [props.user, project]);

  const onTagSelect = async (tagId: string) => {
    const tag = project?.tags?.find((x) => x.id === tagId);
    if (!tag || !project) {
      return;
    }
    const newTags: ProjectUserTagV1Model[] = [
      ...userTags,
      { permission: 'READ', tagId: tag.id, projectId: project.id },
    ];
    setShowDropdown(false);
    await updateTags(newTags);
  };

  const onDeleteIconClick = async (tagId: string) => {
    const newTags = userTags.filter((x) => x.tagId !== tagId);
    await updateTags(newTags);
  };

  const toggleTag = async (tagId: string) => {
    const newTags: ProjectUserTagV1Model[] = userTags.map((x) => {
      return x.tagId === tagId
        ? {
            ...x,
            permission: x.permission === 'READ' ? 'WRITE' : 'READ',
          }
        : x;
    });

    await updateTags(newTags);
  };

  const updateTags = async (tags: ProjectUserTagV1Model[]) => {
    try {
      setStatus(Status.Saving);

      await SkyMapAxiosServiceFactory.instance.createProjectServiceV0().putProjectUserTagsV1({
        path: {
          projectId: project!.id,
          userId: props.user.id,
        },
        body: {
          data: tags,
        },
      });

      setUserTags(tags);
      setStatus(Status.Idle);
    } catch (err) {
      setStatus(Status.Error);
      publish(SubscriptionTopic.ToastrMessage, {
        message: t('projectUserTags.saveError', { ns: 'components' }),
        type: 'error',
      });
    }
  };

  const dropDownTags = project
    ? project.tags.filter((x) => userTags.every((ut) => ut.tagId !== x.id))
    : [];

  const renderContent = () => {
    if (status === Status.Loading) {
      return t('projectUserTags.loading', { ns: 'components' });
    }

    if (status === Status.Error && userTags.length === 0) {
      return (
        <InfoBox color="red">{t('projectUserTags.loadingError', { ns: 'components' })}</InfoBox>
      );
    }

    const addedTags = project!.tags
      .filter((x) => userTags.some((userTag) => userTag.tagId === x.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return addedTags.length > 0 ? (
      <Stack direction="column" spacing={0.35}>
        {addedTags.map((x) => {
          const userTag = userTags.find((userTag) => userTag.tagId === x.id)!;
          const isWrite = userTag.permission === 'WRITE';

          return (
            <TagRow
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              key={x.id}
              spacing={0.75}
            >
              <TagName>{translateDbTagName(x.name, t)}</TagName>
              <Stack alignItems="center" direction="row" spacing={0.5}>
                <PermissionLabel>
                  {isWrite
                    ? t('projectUserTags.writePermission.title', { ns: 'components' })
                    : t('projectUserTags.readPermission.title', { ns: 'components' })}
                </PermissionLabel>
                <Switch
                  checked={isWrite}
                  colorOnChecked={false}
                  size={0.7}
                  onChange={() => toggleTag(x.id)}
                />
                <DeleteButton
                  title={t('projectUserTags.disconnect', { ns: 'components' })}
                  onClick={() => onDeleteIconClick(x.id)}
                >
                  <Icon icon={['fad', 'trash']} />
                </DeleteButton>
              </Stack>
            </TagRow>
          );
        })}
      </Stack>
    ) : (
      <NoTags>{t('projectUserTags.noTags', { ns: 'components' })}</NoTags>
    );
  };

  return (
    <Component>
      <HeaderRow>
        <HeaderLabel>
          {t('projectUserTags.title', { ns: 'components' })}
          <Tooltip content={<ProjectUserTagsInfo userName={props.user.name} />}>
            <InfoIcon color="#00a9e0" icon={['fas', 'circle-info']} />
          </Tooltip>
        </HeaderLabel>
        {dropDownTags.length > 0 && (
          <AddTagButton
            ref={refs.setReference}
            title={t('projectUserTags.addTag', { ns: 'components' })}
            onClick={() => setShowDropdown((x) => !x)}
          >
            <Icon icon={['fas', 'plus']} />
          </AddTagButton>
        )}
      </HeaderRow>
      <OverlayLoader visible={status === Status.Saving}>
        <TagsContainer>{project ? renderContent() : null}</TagsContainer>
      </OverlayLoader>
      {showDropdown &&
        dropDownTags.length > 0 &&
        createPortal(
          <TagDropdownMenu ref={refs.setFloating} role="menu" style={floatingStyles}>
            {dropDownTags.map((x) => (
              <TagDropdownItem key={x.id} role="menuitem" onClick={() => onTagSelect(x.id)}>
                {translateDbTagName(x.name, t)}
              </TagDropdownItem>
            ))}
          </TagDropdownMenu>,
          document.body,
        )}
    </Component>
  );
};

const Component = styled.div`
  ${reset}

  font-size: 0.9em;

  strong {
    font-weight: 500;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5em;
`;

const HeaderLabel = styled.span`
  font-size: calc(0.95em / 0.9);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5em;
`;

const InfoIcon = styled(Icon)`
  font-size: 0.9em;
`;

const AddTagButton = styled.button`
  background: none;
  border: none;
  padding: 0.2em 0.3em;
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  border-radius: 0.2em;
  transition: background-color 0.15s ease-in-out;
  font-size: inherit;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const TagsContainer = styled.div``;

const TagRow = styled(Stack)`
  padding: 0.4em 0.6em;
  border: 1px solid rgba(116, 118, 120, 0.2);
  border-radius: 0.25em;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const TagName = styled.span`
  font-weight: 500;
`;

const PermissionLabel = styled.span`
  font-size: 0.85em;
  color: #666;
  min-width: 5em;
  text-align: left;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  border-radius: 0.2em;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;

  ${TagRow}:hover & {
    opacity: 1;
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const NoTags = styled.div`
  font-style: italic;
  font-size: 0.9em;
`;

const TagDropdownMenu = styled.div`
  background: #fff;
  border: 1px solid rgba(116, 118, 120, 0.25);
  border-radius: 0.3em;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.06);
  max-width: min-content;
  padding: 0.3em 0;
  z-index: 99999;
  font-size: 0.9em;
  overflow: hidden;
`;

const TagDropdownItem = styled.button`
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  padding: 0.45em 0.8em;
  cursor: pointer;
  font-weight: 500;
  color: inherit;
  font-size: inherit;
  transition: background-color 0.1s ease-in-out;

  &:hover,
  &:focus-visible {
    background-color: #f5f5f5;
    outline: none;
  }
`;

// TODO: Refactor database value.
// https://skymapinnovations.atlassian.net/browse/SK-5009
export const translateDbTagName = (tagName: string, t: TFunction) => {
  switch (tagName.trim().toLowerCase()) {
    case 'beställare':
      return t('projectUserTags.tags.client', { ns: 'components' });
    case 'projektledare':
      return t('projectUserTags.tags.projectManager', { ns: 'components' });
    case 'projektör':
      return t('projectUserTags.tags.projectDesigner', { ns: 'components' });
    case 'entreprenör':
      return t('projectUserTags.tags.contractor', { ns: 'components' });
    case 'fastighetsägare':
      return t('projectUserTags.tags.propertyOwner', { ns: 'components' });
    case 'hyresgäst':
      return t('projectUserTags.tags.tenant', { ns: 'components' });
    case 'fastighetsskötare':
      return t('projectUserTags.tags.propertyManager', { ns: 'components' });
    case 'gäst':
      return t('projectUserTags.tags.guest', { ns: 'components' });
    case 'publik':
      return t('projectUserTags.tags.public', { ns: 'components' });
  }

  return '';
};

interface InfoProps {
  userName: string;
}

const ProjectUserTagsInfo = ({ userName }: InfoProps) => {
  const { project } = useProjectSnapshot();
  const { t } = useTranslation();

  if (!project) {
    return null;
  }

  return (
    <InfoContainer>
      <p>
        <Trans
          components={{
            bold: <strong />,
            br: <br />,
          }}
          i18nKey="projectUserTags.info"
          ns="components"
          values={{
            userName,
            projectName: project.name,
          }}
        />
      </p>

      <strong>{t('projectUserTags.permissionsTitle', { ns: 'components' })}</strong>

      <table>
        <tbody>
          <tr>
            <td>
              <strong>{t('projectUserTags.readPermission.title', { ns: 'components' })}</strong>
            </td>
            <td>{t('projectUserTags.readPermission.text', { ns: 'components' })}</td>
          </tr>
          <tr>
            <td>
              <strong>{t('projectUserTags.writePermission.title', { ns: 'components' })}</strong>
            </td>
            <td>
              <Trans
                components={{
                  bold: <strong />,
                }}
                i18nKey="projectUserTags.writePermission.text"
                ns="components"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </InfoContainer>
  );
};

const InfoContainer = styled.div`
  line-height: 1.2em;

  p {
    margin: 0 0 0.5em;
  }

  table {
    margin-top: 0.5em;
    font-size: 0.9em;
  }

  td:first-child {
    padding-right: 2em;
  }

  tr:not(:last-child) td {
    padding-bottom: 0.5em;
  }
`;

export { ProjectUserTags, ProjectUserTagsInfo };
